import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Copy, Check, FileText, ShoppingBag, Users, MessageSquare, RefreshCw, Wand2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toneOptions = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'creative', label: 'Creative' },
  { value: 'bold', label: 'Bold' },
];

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button data-testid="copy-ai-result" onClick={() => { navigator.clipboard.writeText(text); setCopied(true); toast.success('Copied!'); setTimeout(() => setCopied(false), 2000); }}
      className="p-1.5 rounded-md hover:bg-white/10 text-[#71717A] hover:text-white transition-all">
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const ResultCard = ({ text, index }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
    className="bg-[#0a0a0a] border border-[#27272A] rounded-xl p-4 group hover:border-violet-500/30 transition-all">
    <div className="flex items-start justify-between gap-2">
      <p className="text-[#EDEDED] text-sm leading-relaxed flex-1">{text}</p>
      <CopyButton text={text} />
    </div>
  </motion.div>
);

// ===== BIO WRITER TAB =====
const BioWriter = () => {
  const [bioType, setBioType] = useState('bio');
  const [tone, setTone] = useState('professional');
  const [industry, setIndustry] = useState('');
  const [currentBio, setCurrentBio] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/generate-bio`, { bio_type: bioType, tone, industry, current_bio: currentBio });
      setResults(res.data.options || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        {['bio', 'tagline', 'about'].map((t) => (
          <button key={t} data-testid={`bio-type-${t}`} onClick={() => { setBioType(t); setResults([]); }}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${bioType === t ? 'bg-violet-500/20 text-violet-400 border border-violet-500/40' : 'bg-[#121212] text-[#A1A1AA] border border-[#27272A] hover:border-[#3f3f46]'}`}>
            {t === 'bio' ? 'Profile Bio' : t === 'tagline' ? 'Tagline' : 'About Section'}
          </button>
        ))}
      </div>
      <div>
        <Label className="text-[#EDEDED] text-sm">Industry / Niche</Label>
        <Input data-testid="bio-industry" value={industry} onChange={(e) => setIndustry(e.target.value)}
          className="mt-1.5 bg-[#121212] border-[#27272A] text-white h-10 text-sm" placeholder="e.g. Real Estate, Tech, Consulting" />
      </div>
      <div>
        <Label className="text-[#EDEDED] text-sm">Current {bioType} (optional, for reference)</Label>
        <textarea data-testid="bio-current" value={currentBio} onChange={(e) => setCurrentBio(e.target.value)}
          className="mt-1.5 w-full bg-[#121212] border border-[#27272A] rounded-md text-white text-sm p-3 min-h-[80px] resize-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none"
          placeholder={`Paste your current ${bioType} here if you want Claude to improve it...`} />
      </div>
      <div>
        <Label className="text-[#EDEDED] text-sm">Tone</Label>
        <div className="flex gap-2 mt-1.5 flex-wrap">
          {toneOptions.map((t) => (
            <button key={t.value} data-testid={`tone-${t.value}`} onClick={() => setTone(t.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${tone === t.value ? 'bg-violet-500 text-white' : 'bg-[#1a1a1a] text-[#A1A1AA] hover:bg-[#222]'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <Button data-testid="generate-bio-btn" onClick={generate} disabled={loading}
        className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-medium">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
        Generate {bioType === 'bio' ? 'Bio' : bioType === 'tagline' ? 'Taglines' : 'About'}
      </Button>
      {results.length > 0 && (
        <div className="space-y-3 mt-4">
          <p className="text-xs text-[#71717A] uppercase tracking-wider">Results — click to copy</p>
          {results.map((r, i) => <ResultCard key={i} text={r} index={i} />)}
        </div>
      )}
    </div>
  );
};

// ===== CONTENT GENERATOR TAB =====
const ContentGenerator = () => {
  const [contentType, setContentType] = useState('link_description');
  const [title, setTitle] = useState('');
  const [context, setContext] = useState('');
  const [tone, setTone] = useState('professional');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/generate-content`, { content_type: contentType, title, context, tone });
      setResults(res.data.options || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2">
        {[
          { value: 'link_description', label: 'Link' },
          { value: 'event_description', label: 'Event' },
          { value: 'product_description', label: 'Product' },
        ].map((t) => (
          <button key={t.value} data-testid={`content-type-${t.value}`} onClick={() => { setContentType(t.value); setResults([]); }}
            className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${contentType === t.value ? 'bg-violet-500/20 text-violet-400 border border-violet-500/40' : 'bg-[#121212] text-[#A1A1AA] border border-[#27272A] hover:border-[#3f3f46]'}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div>
        <Label className="text-[#EDEDED] text-sm">Title / Name</Label>
        <Input data-testid="content-title" value={title} onChange={(e) => setTitle(e.target.value)}
          className="mt-1.5 bg-[#121212] border-[#27272A] text-white h-10 text-sm" placeholder="e.g. Premium Consulting Package" />
      </div>
      <div>
        <Label className="text-[#EDEDED] text-sm">Additional Context (optional)</Label>
        <textarea data-testid="content-context" value={context} onChange={(e) => setContext(e.target.value)}
          className="mt-1.5 w-full bg-[#121212] border border-[#27272A] rounded-md text-white text-sm p-3 min-h-[60px] resize-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none"
          placeholder="Add details like features, pricing, date, audience..." />
      </div>
      <div className="flex gap-2 flex-wrap">
        {toneOptions.map((t) => (
          <button key={t.value} data-testid={`content-tone-${t.value}`} onClick={() => setTone(t.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${tone === t.value ? 'bg-violet-500 text-white' : 'bg-[#1a1a1a] text-[#A1A1AA] hover:bg-[#222]'}`}>
            {t.label}
          </button>
        ))}
      </div>
      <Button data-testid="generate-content-btn" onClick={generate} disabled={loading || !title.trim()}
        className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-medium">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
        Generate Description
      </Button>
      {results.length > 0 && (
        <div className="space-y-3 mt-4">
          <p className="text-xs text-[#71717A] uppercase tracking-wider">Results — click to copy</p>
          {results.map((r, i) => <ResultCard key={i} text={r} index={i} />)}
        </div>
      )}
    </div>
  );
};

// ===== LEAD INSIGHTS TAB =====
const LeadInsights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/lead-insights`, {});
      setInsights(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-[#A1A1AA] text-sm">Claude AI will analyze your recent leads and provide actionable insights, priorities, and follow-up recommendations.</p>
      <Button data-testid="analyze-leads-btn" onClick={analyze} disabled={loading}
        className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-medium">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
        Analyze My Leads
      </Button>
      {insights && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Summary */}
          {insights.summary && (
            <div className="bg-[#0a0a0a] border border-[#27272A] rounded-xl p-4">
              <h4 className="text-white font-medium text-sm mb-3">Summary</h4>
              <div className="flex gap-4 flex-wrap">
                <div className="bg-[#1a1a1a] rounded-lg px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-violet-400">{insights.lead_count || insights.summary.total || 0}</p>
                  <p className="text-xs text-[#71717A]">Total Leads</p>
                </div>
                {insights.summary.avg_quality && (
                  <div className="bg-[#1a1a1a] rounded-lg px-4 py-2 text-center">
                    <p className="text-2xl font-bold text-green-400">{insights.summary.avg_quality}/10</p>
                    <p className="text-xs text-[#71717A]">Quality Score</p>
                  </div>
                )}
              </div>
              {insights.summary.top_sources?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-[#71717A] mb-1">Top Sources</p>
                  <div className="flex gap-2 flex-wrap">
                    {insights.summary.top_sources.map((s, i) => (
                      <span key={i} className="px-2 py-1 rounded-md bg-violet-500/10 text-violet-400 text-xs">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Insights text */}
          {insights.insights && (
            <div className="bg-[#0a0a0a] border border-[#27272A] rounded-xl p-4">
              <h4 className="text-white font-medium text-sm mb-2">Insights</h4>
              <p className="text-[#EDEDED] text-sm leading-relaxed">{typeof insights.insights === 'string' ? insights.insights : JSON.stringify(insights.insights)}</p>
            </div>
          )}
          {/* Recommendations */}
          {insights.recommendations?.length > 0 && (
            <div className="bg-[#0a0a0a] border border-[#27272A] rounded-xl p-4">
              <h4 className="text-white font-medium text-sm mb-2">Recommendations</h4>
              <ul className="space-y-2">
                {insights.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#EDEDED]">
                    <span className="text-violet-400 font-bold mt-0.5">{i + 1}.</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Priority Leads */}
          {insights.priority_leads?.length > 0 && (
            <div className="bg-[#0a0a0a] border border-[#27272A] rounded-xl p-4">
              <h4 className="text-white font-medium text-sm mb-2">Priority — Contact First</h4>
              <div className="flex gap-2 flex-wrap">
                {insights.priority_leads.map((name, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 text-xs font-medium">{name}</span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ===== SMART REPLY TAB =====
const SmartReply = () => {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/leads`);
        setLeads(res.data.filter(l => l.message));
      } catch {
        // ignore
      } finally {
        setLoadingLeads(false);
      }
    })();
  }, []);

  const generateReply = async (lead) => {
    setSelectedLead(lead);
    setLoading(true);
    setReplies([]);
    try {
      const res = await axios.post(`${API}/ai/smart-reply`, {
        lead_id: lead.id,
        message: lead.message,
        sender_name: lead.name || ''
      });
      setReplies(res.data.replies || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate replies');
    } finally {
      setLoading(false);
    }
  };

  if (loadingLeads) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-violet-400" /></div>;

  return (
    <div className="space-y-5">
      {leads.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="w-10 h-10 text-[#3f3f46] mx-auto mb-3" />
          <p className="text-[#71717A] text-sm">No leads with messages yet. Once customers send you messages, Claude will help you craft replies.</p>
        </div>
      ) : (
        <>
          <p className="text-[#A1A1AA] text-sm">Select a lead message and Claude will generate 3 reply options for you.</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {leads.slice(0, 20).map((lead) => (
              <button key={lead.id} data-testid={`lead-${lead.id}`} onClick={() => generateReply(lead)}
                className={`w-full text-left p-3 rounded-xl border transition-all ${selectedLead?.id === lead.id ? 'bg-violet-500/10 border-violet-500/40' : 'bg-[#0a0a0a] border-[#27272A] hover:border-[#3f3f46]'}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white text-sm font-medium">{lead.name || 'Anonymous'}</p>
                  <span className="text-[#71717A] text-xs">{lead.created_at?.slice(0, 10)}</span>
                </div>
                <p className="text-[#A1A1AA] text-xs line-clamp-2">{lead.message}</p>
              </button>
            ))}
          </div>
          {loading && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
              <span className="text-[#A1A1AA] text-sm">Claude is writing replies...</span>
            </div>
          )}
          {replies.length > 0 && !loading && (
            <div className="space-y-3">
              <p className="text-xs text-[#71717A] uppercase tracking-wider">Reply Options — click to copy</p>
              {replies.map((r, i) => <ResultCard key={i} text={r} index={i} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ===== MAIN AI ASSISTANT COMPONENT =====
const AIAssistant = () => {
  const [activeTab, setActiveTab] = useState('bio');

  const tabs = [
    { id: 'bio', label: 'Bio Writer', icon: FileText },
    { id: 'content', label: 'Content', icon: ShoppingBag },
    { id: 'leads', label: 'Lead Insights', icon: Users },
    { id: 'reply', label: 'Smart Reply', icon: MessageSquare },
  ];

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white" data-testid="ai-assistant-title">AI Assistant</h2>
          <p className="text-[#71717A] text-xs">Powered by Claude</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 bg-[#0a0a0a] border border-[#27272A] rounded-xl p-1 mb-6">
        {tabs.map((tab) => (
          <button key={tab.id} data-testid={`ai-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-violet-500/20 text-violet-400' : 'text-[#71717A] hover:text-[#A1A1AA]'
            }`}>
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
          {activeTab === 'bio' && <BioWriter />}
          {activeTab === 'content' && <ContentGenerator />}
          {activeTab === 'leads' && <LeadInsights />}
          {activeTab === 'reply' && <SmartReply />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AIAssistant;
